import { AgxCurrency } from '@urbanx/agx-ui-components';
import { AgxRadioGroup, AgxSpacer } from '@urbanx/agx.ui-components2';
import {
  StaffGetSaleInformationResponseSaleMethod,
  StaffGetSaleInformationResponseUnconditionalDisplayAs,
  StaffGetSaleInformationResponseUnderOfferDisplayAs,
  type StaffGetSaleInformationResponse,
  StaffUpdateSaleInformationRequest,
  StaffUpdateSaleInformationRequestSaleMethod,
  StaffUpdateSaleInformationRequestUnconditionalDisplayAs,
  StaffUpdateSaleInformationRequestUnderOfferDisplayAs,
  useInvalidateUseStaffGetSaleInformation,
  useStaffGetSaleInformation,
  useStaffUpdateSaleInformation,
  useInvalidateUseStaffGetContractDeposits,
  useInvalidateUseStaffGetContract,
} from '@urbanx/agx.campaigns.client';
import { useQueryClient } from '@tanstack/react-query';
import { useSelectedContract } from 'hooks';
import { Pages } from 'constants/menu';
import { SubPagePanel } from 'components/panel/SubPagePanel';
import { RexDisconnectedModalFields } from 'pages/campaigns/components/manageCampaign/RexDataUpdateHelper';
import { useGetListingByCampaignId } from '@urbanx/agx.rex.client';
import { isEqual } from 'lodash-es';
import {
  ContractSettingsUnconditionalDisplayAs,
  ContractSettingsUnderOfferDisplayAs,
} from '@urbanx/agx.agencies.client';
import useRexDisconnectedModal from 'pages/campaigns/components/manageCampaign/useRexDisconnectedModal';
import { ContractInformationPage } from './ContractInformationPage';
import { useContractInformationEditor } from './useContractInformationEditor';

function toRequestUnderOfferDisplayAs(
  value: StaffGetSaleInformationResponseUnderOfferDisplayAs | undefined
): StaffUpdateSaleInformationRequestUnderOfferDisplayAs | undefined {
  switch (value) {
    case StaffGetSaleInformationResponseUnderOfferDisplayAs.Available:
      return StaffUpdateSaleInformationRequestUnderOfferDisplayAs.Available;
    case StaffGetSaleInformationResponseUnderOfferDisplayAs.UnderOffer:
      return StaffUpdateSaleInformationRequestUnderOfferDisplayAs.UnderOffer;
    default:
      return undefined;
  }
}

function toRequestUnconditionalDisplayAs(
  value: StaffGetSaleInformationResponseUnconditionalDisplayAs | undefined
): StaffUpdateSaleInformationRequestUnconditionalDisplayAs | undefined {
  switch (value) {
    case StaffGetSaleInformationResponseUnconditionalDisplayAs.Sold:
      return StaffUpdateSaleInformationRequestUnconditionalDisplayAs.Sold;
    case StaffGetSaleInformationResponseUnconditionalDisplayAs.UnderOffer:
      return StaffUpdateSaleInformationRequestUnconditionalDisplayAs.UnderOffer;
    case StaffGetSaleInformationResponseUnconditionalDisplayAs.DoNotUpdate:
      return StaffUpdateSaleInformationRequestUnconditionalDisplayAs.DoNotUpdate;
    default:
      return undefined;
  }
}

const SaleInformation = () => {
  const { contract, campaignId } = useSelectedContract();
  const invalidateUseStaffGetSaleInformation = useInvalidateUseStaffGetSaleInformation();
  const invalidateUseStaffGetContractDeposits = useInvalidateUseStaffGetContractDeposits();
  const invalidateUseStaffGetContract = useInvalidateUseStaffGetContract();
  const queryClient = useQueryClient();

  const {
    data: saleInformation,
    isPending,
    isError,
  } = useStaffGetSaleInformation(
    campaignId || '',
    contract?.contractId || '',
    !!campaignId && !!contract?.contractId
  );

  const { modal: rexDisconnectedModal, openWithChanges } = useRexDisconnectedModal();

  const { staffUpdateSaleInformation } = useStaffUpdateSaleInformation({
    onSuccess: () => {},
    onError: () => {},
  });

  const { data: rexListing, isPending: isPendingRexListing } = useGetListingByCampaignId(
    campaignId || '',
    campaignId !== undefined
  );

  type SaleInformationDraft = {
    salePrice: number | undefined;
    displaySalePrice: boolean;
    saleMethod: StaffGetSaleInformationResponseSaleMethod;
    underOfferDisplayAs: StaffGetSaleInformationResponseUnderOfferDisplayAs | undefined;
    unconditionalDisplayAs: StaffGetSaleInformationResponseUnconditionalDisplayAs | undefined;
  };

  const toDraft = (response: StaffGetSaleInformationResponse): SaleInformationDraft => ({
    salePrice: response.salePrice ?? undefined,
    displaySalePrice: response.displaySalePrice ?? false,
    saleMethod: response.saleMethod ?? StaffGetSaleInformationResponseSaleMethod.PrivateTreaty,
    underOfferDisplayAs: response.underOfferDisplayAs,
    unconditionalDisplayAs: response.unconditionalDisplayAs,
  });

  const source = saleInformation ? toDraft(saleInformation) : undefined;

  const editor = useContractInformationEditor({
    resourceKey: `${campaignId ?? ''}:${contract?.contractId ?? ''}:sale-information`,
    source,
    isPending,
    isError,
    persist: async ({ baseline, draft }) => {
      const request: StaffUpdateSaleInformationRequest = {
        displaySalePrice: draft.displaySalePrice,
        salePrice: draft.salePrice,
        underOfferDisplayAs: toRequestUnderOfferDisplayAs(draft.underOfferDisplayAs),
        unconditionalDisplayAs: toRequestUnconditionalDisplayAs(draft.unconditionalDisplayAs),
      };

      if (draft.saleMethod === StaffGetSaleInformationResponseSaleMethod.Auction) {
        request.saleMethod = StaffUpdateSaleInformationRequestSaleMethod.Auction;
      } else {
        request.saleMethod = StaffUpdateSaleInformationRequestSaleMethod.PrivateTreaty;
      }

      const response = await staffUpdateSaleInformation({
        body: request,
        campaignId: campaignId!,
        contractId: contract!.contractId,
      });

      if (!response?.success) return { accepted: false, errors: response?.errors ?? [] };
      if (!response.saleInformation) throw new Error('Canonical sale information is required');

      queryClient.setQueryData<StaffGetSaleInformationResponse>(
        ['staffGetSaleInformation', campaignId!, contract!.contractId],
        response.saleInformation
      );

      if (!rexListing && isPendingRexListing === false) {
        openWithChanges(
          !isEqual(baseline.salePrice, draft.salePrice)
            ? [{ field: RexDisconnectedModalFields.SalePrice, value: draft.salePrice }]
            : []
        );
      }

      return { accepted: true, baseline: toDraft(response.saleInformation) };
    },
    refresh: {
      authority: 'mutation',
      refreshSource: () => invalidateUseStaffGetSaleInformation(campaignId!, contract!.contractId),
      refreshDependencies: [
        () => invalidateUseStaffGetContractDeposits(campaignId!, contract!.contractId),
        () => invalidateUseStaffGetContract(contract!.contractId),
      ],
    },
  });

  if (!editor.draft) {
    return (
      <ContractInformationPage currentPage={Pages.SALE_INFORMATION} editor={editor}>
        {null}
      </ContractInformationPage>
    );
  }

  const { draft } = editor;

  return (
    <ContractInformationPage currentPage={Pages.SALE_INFORMATION} editor={editor}>
      <SubPagePanel
        key={`SaleInformationSalePrice${isPending ? 'Loaded' : ''}`}
        panelTitle="Sale Price"
        expandable
        defaultExpanded
      >
        <AgxCurrency
          key={`saleInformation_SalePrice${isPending ? 'Loaded' : ''}`}
          id="saleInformation_SalePrice"
          required
          onInputValueChange={({ value: updatedValue }) => {
            editor.update(
              'salePrice',
              updatedValue ? parseFloat(updatedValue) || undefined : undefined
            );
          }}
          stretch
          defaultValue={draft.salePrice?.toString() || ''}
          validate={editor.dirty}
          decimalPoints={2}
          displayErrors={true}
        />
      </SubPagePanel>
      <SubPagePanel
        key={`SaleInformationSaleMethod${isPending ? 'Loaded' : ''}`}
        panelTitle="Sale Method"
        expandable
        defaultExpanded
      >
        <AgxRadioGroup
          key={`saleInformation_SaleMethod${isPending ? 'Loaded' : ''}`}
          id="saleInformation_SaleMethod"
          layout="stacked"
          options={[
            {
              value: StaffGetSaleInformationResponseSaleMethod.PrivateTreaty,
              label: 'Private Treaty',
            },
            {
              value: StaffGetSaleInformationResponseSaleMethod.Auction,
              label: 'Auction',
            },
          ]}
          onChange={(val) => {
            editor.update('saleMethod', val as StaffGetSaleInformationResponseSaleMethod);
          }}
          value={draft.saleMethod}
        />
      </SubPagePanel>
      <SubPagePanel
        key={`SaleInformationDisplaySalePrice${isPending ? 'Loaded' : ''}`}
        panelTitle="Display Sale Price"
        expandable
        defaultExpanded
      >
        <AgxRadioGroup
          key={`saleInformation_DisplaySalePrice${isPending ? 'Loaded' : ''}`}
          id="saleInformation_DisplaySalePrice"
          layout="stacked"
          options={[
            {
              value: 'Yes',
              label: 'Yes, display sale price',
            },
            {
              value: 'No',
              label: 'No, hide sale price',
            },
          ]}
          onChange={(val) => {
            editor.update('displaySalePrice', val === 'Yes');
          }}
          value={draft.displaySalePrice ? 'Yes' : 'No'}
          label="Display sale price once marked as SOLD?"
        />
      </SubPagePanel>
      <SubPagePanel
        key={`SaleInformationRexListingStatusPreference${isPending ? 'Loaded' : ''}`}
        panelTitle="REX Listing Status Preference"
        expandable
        defaultExpanded
      >
        <AgxSpacer gap="md">
          <AgxRadioGroup
            key={`saleInformation_DisplayUnderOffer${isPending ? 'Loaded' : ''}`}
            id="saleInformation_DisplayUnderOffer"
            layout="stacked"
            options={[
              {
                value: ContractSettingsUnderOfferDisplayAs.Available,
                label: 'Available',
              },
              {
                value: ContractSettingsUnderOfferDisplayAs.UnderOffer,
                label: 'Under Offer',
              },
            ]}
            onChange={(val) => {
              editor.update(
                'underOfferDisplayAs',
                val as StaffGetSaleInformationResponseUnderOfferDisplayAs
              );
            }}
            value={draft.underOfferDisplayAs}
            label="When Contract “Accepted”, set listing status to:"
          />
          <AgxRadioGroup
            key={`saleInformation_DisplayUnconditional${isPending ? 'Loaded' : ''}`}
            id="saleInformation_DisplayUnconditional"
            layout="stacked"
            options={[
              {
                value: ContractSettingsUnconditionalDisplayAs.UnderOffer,
                label: 'Under Offer',
              },
              {
                value: ContractSettingsUnconditionalDisplayAs.Sold,
                label: 'Sold',
              },
              {
                value: ContractSettingsUnconditionalDisplayAs.DoNotUpdate,
                label: 'Do not update listing status',
              },
            ]}
            onChange={(val) => {
              editor.update(
                'unconditionalDisplayAs',
                val as StaffGetSaleInformationResponseUnconditionalDisplayAs
              );
            }}
            value={draft.unconditionalDisplayAs}
            label="When Contract “Unconditional”, set listing status to:"
          />
        </AgxSpacer>
      </SubPagePanel>
      {rexDisconnectedModal}
    </ContractInformationPage>
  );
};

export default SaleInformation;
